import * as React from 'react';

import MuiFormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MuiRadioGroup, { RadioGroupProps, useRadioGroup } from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';

import styled from '@emotion/styled';
import { useField } from 'formik';

import SvgIcon from '../../../../../components/Icons';
import { Requirement } from '../../schema';
import RadioContent from './RadioContent';

const FormControlLabel: React.FC<FormControlLabelProps> = (props) => {
  const radioGroup = useRadioGroup();
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    if (radioGroup) {
      setChecked(radioGroup.value === props.value);
    }
  }, [radioGroup, props.value]);

  return (
    <MuiFormControlLabel
      checked={checked}
      {...props}
      sx={{
        margin: 0,
        width: '100%',
        '.MuiButtonBase-root': { p: 0 },
        '&.Mui-disabled': {
          opacity: '0.4',
          borderRadius: '10px',
          border: '1px solid #ECECEC',
          backgroundColor: '#ccc',
        },
      }}
    />
  );
};

const getMinOfferValue = (requirement: Requirement): number => {
  switch (requirement) {
    case 'postOnInstagramStories':
      return 0;
    case 'postOnInstagramStories2x':
      return 25;
    case 'postOnInstagramStories3x':
      return 50;
    case 'postOnInstagramFeed':
      return 100;
    case 'postOnInstagramReels':
      return 200;
    default:
      return 0;
  }
};

type DisabledTooltipWrapperProps = {
  children: React.ReactElement;
  disabled: boolean;
  minValue: number;
};

const DisabledTooltipWrapper: React.FC<DisabledTooltipWrapperProps> = ({
  children,
  disabled,
  minValue,
}) => {
  if (!disabled) {
    return children;
  }

  return (
    <Tooltip
      title={`Requires $${minValue}+ offer value`}
      arrow
      placement='top'
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: '#fff',
            color: '#303030',
            fontFamily: 'Poppins',
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '21px',
            letterSpacing: 0,
            padding: '10px 16px',
            borderRadius: '8px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          },
        },
        arrow: {
          sx: {
            color: '#fff',
          },
        },
      }}
    >
      <span style={{ display: 'block', width: '100%' }}>{children}</span>
    </Tooltip>
  );
};

const OfferRequirement: React.FC<
  RadioGroupProps & { offerValue?: number; noPremiumAccess?: boolean }
> = ({ id, offerValue, noPremiumAccess = false }) => {
  const [field, , helper] = useField<Requirement>('requirements');

  const isDisabledByOfferValue = React.useCallback(
    (requirement: Requirement): boolean => {
      if (offerValue !== undefined) {
        return offerValue < getMinOfferValue(requirement);
      }
      return false;
    },
    [offerValue],
  );

  const isDisabled = React.useCallback(
    (requirement: Requirement): boolean => {
      // If offerValue is provided, use value-based logic
      if (offerValue !== undefined) {
        return offerValue < getMinOfferValue(requirement);
      }
      // Fall back to premium access logic for backward compatibility
      return noPremiumAccess;
    },
    [offerValue, noPremiumAccess],
  );

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const v = e.currentTarget.value as Requirement;
      helper.setValue(v);
    },
    [helper],
  );

  return (
    <RadioWraper>
      <MuiRadioGroup id={id} value={field.value} onChange={handleChange}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControlLabel
              value='postOnInstagramStories'
              control={
                <RadioContent
                  label='Post on Story'
                  icon={<SvgIcon name='StoryBlack' />}
                  checkedIcon={<SvgIcon name='Story' />}
                />
              }
              label=''
            />
          </Grid>
          <Grid item xs={6}>
            <DisabledTooltipWrapper
              disabled={isDisabledByOfferValue('postOnInstagramStories2x')}
              minValue={getMinOfferValue('postOnInstagramStories2x')}
            >
              <FormControlLabel
                value='postOnInstagramStories2x'
                control={
                  <RadioContent
                    label='Post 2 Stories'
                    icon={<SvgIcon name='StoryBlack' />}
                    checkedIcon={<SvgIcon name='Story' />}
                  />
                }
                label=''
                disabled={isDisabled('postOnInstagramStories2x')}
              />
            </DisabledTooltipWrapper>
          </Grid>
          <Grid item xs={6}>
            <DisabledTooltipWrapper
              disabled={isDisabledByOfferValue('postOnInstagramStories3x')}
              minValue={getMinOfferValue('postOnInstagramStories3x')}
            >
              <FormControlLabel
                value='postOnInstagramStories3x'
                control={
                  <RadioContent
                    label='Post 3 Stories'
                    icon={<SvgIcon name='StoryBlack' />}
                    checkedIcon={<SvgIcon name='Story' />}
                  />
                }
                label=''
                disabled={isDisabled('postOnInstagramStories3x')}
              />
            </DisabledTooltipWrapper>
          </Grid>
          <Grid item xs={6}>
            <DisabledTooltipWrapper
              disabled={isDisabledByOfferValue('postOnInstagramFeed')}
              minValue={getMinOfferValue('postOnInstagramFeed')}
            >
              <FormControlLabel
                value='postOnInstagramFeed'
                control={
                  <RadioContent
                    label='Post on Feed'
                    icon={<SvgIcon name='FeedBlack' />}
                    checkedIcon={<SvgIcon name='Feed' />}
                  />
                }
                label=''
                disabled={isDisabled('postOnInstagramFeed')}
              />
            </DisabledTooltipWrapper>
          </Grid>
          <Grid item xs={6}>
            <DisabledTooltipWrapper
              disabled={isDisabledByOfferValue('postOnInstagramReels')}
              minValue={getMinOfferValue('postOnInstagramReels')}
            >
              <FormControlLabel
                value='postOnInstagramReels'
                control={
                  <RadioContent
                    label='Post on Reel'
                    icon={<SvgIcon name='ReelBlack' />}
                    checkedIcon={<SvgIcon name='Reel' />}
                  />
                }
                label=''
                disabled={isDisabled('postOnInstagramReels')}
              />
            </DisabledTooltipWrapper>
          </Grid>
        </Grid>
      </MuiRadioGroup>
    </RadioWraper>
  );
};

export default OfferRequirement;

const RadioWraper = styled.div`
  .css-1sa2158 {
    border: 1px solid #e6e6e6;
  }
`;
